import { useMemo } from "react";
import * as React from "react";
import {
  Box,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  type SelectChangeEvent,
  IconButton,
  Checkbox as MuiCheckbox,
  ListItemText,
  Chip,
  FormHelperText,
  Divider,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { type ISelectObject } from "@/types/select";
import { SizingWrapper } from "@/components/Wrapper";
import { type IProps as MultiSelectIProps } from "@/components/Select/MultiSelect";

export interface IProps extends Omit<MultiSelectIProps, "maxSelect"> {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: ISelectObject[];
  isClearable?: boolean;
  error?: boolean;
  formHelperText?: string;
}

export const OWNERSHIP_TRANSFER_IDS = ["1", "2", "3", "4", "5", "8"];

const FeedReceptionReasonSelect: React.FC<IProps> = (props) => {
  const {
    label,
    options,
    value,
    onChange,
    isClearable = true,
    size,
    error,
    formHelperText,
  } = props;

  const additionalMenuItemsForMonitoring: ISelectObject[] = [
    { value: "all", label: "選択をクリア" },
    { value: "ownership", label: "所有権移転関連すべて選択" },
  ];

  const extendedOptions = [...additionalMenuItemsForMonitoring, ...options];

  const displayValueList = useMemo(
    () =>
      extendedOptions
        .filter((item) => value.includes(item.value))
        .map((item) => item.label),
    [value, extendedOptions]
  );

  const handleCustomChange = (event: SelectChangeEvent<string[]>): void => {
    const selectedValues = event.target.value as string[];
    const previousValue = [...value];

    if (selectedValues.includes("all")) {
      onChange([]);
      return;
    }

    const isOwnershipClick = selectedValues.includes("ownership");
    if (isOwnershipClick) {
      const isGroupFullySelectedPreviously = OWNERSHIP_TRANSFER_IDS.every(
        (id) => previousValue.includes(id)
      );

      if (isGroupFullySelectedPreviously) {
        onChange([]);
      } else {
        onChange([...OWNERSHIP_TRANSFER_IDS]);
      }
      return;
    }

    const currentSelection = selectedValues.filter(
      (val) => val !== "all" && val !== "ownership"
    );

    const wasGroupFullySelected = OWNERSHIP_TRANSFER_IDS.every((id) =>
      previousValue.includes(id)
    );

    if (wasGroupFullySelected) {
      onChange([]);
      return;
    }

    const selectedValue = currentSelection[currentSelection.length - 1];
    onChange(selectedValue ? [selectedValue] : []);
  };

  const handleClearClick = (): void => {
    onChange([]);
  };

  const getDisabled = (option: ISelectObject): boolean => {
    if (["all", "ownership"].includes(option.value)) {
      return false;
    }
    const isGroupSelected = OWNERSHIP_TRANSFER_IDS.every((id) =>
      value.includes(id)
    );
    if (isGroupSelected && !OWNERSHIP_TRANSFER_IDS.includes(option.value)) {
      return true;
    }
    if (
      value.length >= 1 &&
      !value.includes(option.value) &&
      !isGroupSelected
    ) {
      return true;
    }
    return false;
  };

  return (
    <SizingWrapper size={size}>
      <FormControl fullWidth error={error}>
        <InputLabel id="feed-reception-reason-label">{label}</InputLabel>
        <MuiSelect
          id="feed-reception-reason-id"
          labelId="feed-reception-reason-label"
          label={label}
          onChange={handleCustomChange}
          multiple
          value={value}
          defaultValue={[]}
          error={error}
          sx={{
            "#feed-reception-reason-id": {
              pr: 0,
            },
          }}
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              {displayValueList.map((label) => (
                <Chip key={label} label={label} sx={{ cursor: "pointer" }} />
              ))}
            </Box>
          )}
          endAdornment={
            isClearable ? (
              <IconButton
                sx={{
                  display:
                    displayValueList.length === 0 ? "none" : "inline-flex",
                  marginRight: "10px",
                }}
                onClick={handleClearClick}
              >
                <ClearIcon />
              </IconButton>
            ) : (
              <></>
            )
          }
        >
          {extendedOptions.flatMap((option, index) => [
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={getDisabled(option)}
              sx={{
                marginTop: ["all", "ownership"].includes(option.value)
                  ? "8px"
                  : "0px",
                marginBottom: ["all", "ownership"].includes(option.value)
                  ? "8px"
                  : "0px",
              }}
            >
              {!["all", "ownership"].includes(option.value) && (
                <MuiCheckbox
                  checked={value.some((item) => item === option.value)}
                />
              )}
              <ListItemText primary={option.label} />
            </MenuItem>,
            option.value === "ownership" && (
              <Divider key={`divider-${index}`} />
            ),
          ])}
        </MuiSelect>
        {formHelperText ? (
          <FormHelperText>{formHelperText}</FormHelperText>
        ) : null}
      </FormControl>
    </SizingWrapper>
  );
};

export { FeedReceptionReasonSelect };
